import { memo, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AttachmentKind } from '../../core/attachmentKind';
import { MessageAttachments, type Attachment } from '../../core/messageAttachments';
import type { MessageListRow } from '../../core/messageListDerivation';
import { MessageTimestamp } from '../../core/messageTimestamp';
import { PostEmbed, type PostEmbed as PostEmbedValue } from '../../core/postEmbed';
import type { ReactionChip } from '../../core/reactionsDerivation';
import { PostState, type PostFileRecord, type PostRecord } from '../../core/records';
import { SystemMessageDerivation, type SystemNotice } from '../../core/systemMessageDerivation';
import type { ThreadFooter } from '../../core/threadFooterDerivation';
import { EmojiCharacters } from '../../core/emojiCharacters';
import { Icon } from '../../design/icons';
import { t, tf } from '../../i18n/i18n';
import { Avatar } from '../Avatar';
import { mediaUrls } from '../media';
import type { SeenLevel } from '../models/conversationModel';
import { MessageBody, type BodyContext } from './MessageBody';
import { RelativeTime } from '../../core/relativeTime';

export interface RowActions {
  onReact(postId: string, emojiName: string): void;
  onOpenThread(rootId: string): void;
  onOpenUserId(userId: string, anchor?: { x: number; y: number }): void;
  onRetry(postId: string): void;
  onDiscard(postId: string): void;
  onAction(postId: string, actionId: string, cookie: string | null, selectedOption: string | null): void;
  onCopyText(text: string): void;
  onCopyLink(postId: string): void;
  onPin(postId: string, pinned: boolean): void;
  onSave(postId: string, saved: boolean): void;
  onMarkUnread(postId: string): void;
  onEdit(postId: string, message: string): Promise<boolean>;
  onDelete(postId: string): void;
  quickReactions: string[];
}

export interface RowProps {
  row: MessageListRow;
  authorName: string;
  authorVersion: number;
  presence: string | null;
  isOwn: boolean;
  files: PostFileRecord[] | undefined;
  chips: ReactionChip[] | undefined;
  footer: ThreadFooter | undefined;
  footerNames: (id: string) => { name: string; version: number };
  seen: SeenLevel;
  saved?: boolean;
  marked?: boolean;
  ctx: BodyContext;
  actions: RowActions;
  now: number;
  inThread?: boolean;
}

function timeLabel(ms: number): string {
  return MessageTimestamp.label(ms, (local, home) => `${local} (${home} MX)`);
}

function noticeText(n: SystemNotice): string {
  // actor/subject already arrive in @handle form (SystemMessageDerivation).
  const at = (s: string) => s;
  switch (n.kind) {
    case 'joined':
      return tf('channel.system.joined', at(n.actor));
    case 'left':
      return tf('channel.system.left', at(n.actor));
    case 'added':
      return tf('channel.system.added', at(n.actor), at(n.subject));
    case 'removed':
      return tf('channel.system.removed', at(n.subject));
    case 'headerSet':
      return tf('channel.system.headerSet', at(n.actor), n.detail);
    case 'headerCleared':
      return tf('channel.system.headerCleared', at(n.actor));
    case 'purposeSet':
      return tf('channel.system.purposeSet', at(n.actor), n.detail);
    case 'purposeCleared':
      return tf('channel.system.purposeCleared', at(n.actor));
    case 'renamed':
      return tf('channel.system.renamed', at(n.actor), n.detail);
    case 'archived':
      return n.actor ? tf('channel.system.archived', at(n.actor)) : t('channel.system.archivedGeneric');
    case 'restored':
      return n.actor ? tf('channel.system.restored', at(n.actor)) : t('channel.system.restoredGeneric');
    default:
      return n.detail;
  }
}

export const MessageRow = memo(function MessageRow(p: RowProps) {
  const { row } = p;
  const post = row.post;
  const separators = (
    <>
      {row.dayLabel ? (
        <div className="day-separator" role="separator">
          <span>{row.dayLabel}</span>
        </div>
      ) : null}
      {row.showsUnreadSeparator ? (
        <div className="unread-separator" role="separator">
          <span>{t('channel.unreadSeparator')}</span>
        </div>
      ) : null}
    </>
  );

  if (SystemMessageDerivation.isSystem(post.type)) {
    const notice = SystemMessageDerivation.notice(post.type, post.props, post.message);
    return (
      <>
        {separators}
        <div className="system-row">{notice ? noticeText(notice) : post.message}</div>
      </>
    );
  }

  const props = MessageAttachments.postProps(post.props);
  const isWebhook = props.isFromWebhook;
  const isEphemeral = SystemMessageDerivation.isEphemeral(post.type);
  const hasText = post.message.trim() !== '';
  const embed = post.embedJson ? PostEmbed.fromJson(post.embedJson) : null;
  const pending = post.state === PostState.Pending;
  const failed = post.state === PostState.Failed;
  const [editing, setEditing] = useState(false);
  const [menuAt, setMenuAt] = useState<{ x: number; y: number } | null>(null);
  const openAuthor = (e: React.MouseEvent) => {
    if (isWebhook) return;
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    p.actions.onOpenUserId(post.userId, { x: r.left, y: r.bottom + 6 });
  };

  return (
    <>
      {separators}
      <article
        className={`msg${row.isGroupStart ? ' group-start' : ''}${p.marked ? ' jump-mark' : ''}${menuAt ? ' menu-open' : ''}`}
        data-post-id={post.id}
        aria-label={p.authorName}
        onContextMenu={(e) => {
          if (post.state !== PostState.Synced || isEphemeral || editing) return;
          e.preventDefault();
          setMenuAt({ x: e.clientX, y: e.clientY });
        }}
      >
        <div className="gutter">
          {row.isGroupStart ? (
            <button className="avatar-button" onClick={openAuthor} tabIndex={-1} aria-hidden="true">
              <Avatar userId={post.userId} name={p.authorName} size={32} version={p.authorVersion} presence={isWebhook ? null : p.presence} imageUrl={props.webhookIconUrl || null} />
            </button>
          ) : null}
        </div>
        <div className="content">
          {row.isGroupStart ? (
            <div className="msg-header">
              <button className="author" onClick={openAuthor}>
                {p.authorName}
              </button>
              {isWebhook ? <span className="bot-tag">{t('channel.botBadge')}</span> : null}
              {post.priority ? <span className={`priority-pill ${post.priority}`}>{t(`channel.priority.${post.priority}`)}</span> : null}
              <time className="when" dateTime={new Date(post.createAt).toISOString()}>
                {timeLabel(post.createAt)}
              </time>
            </div>
          ) : null}
          {editing ? (
            <InlineEditor
              initial={post.message}
              onCancel={() => setEditing(false)}
              onSave={async (text) => {
                if (text.trim() === post.message.trim()) return setEditing(false);
                if (await p.actions.onEdit(post.id, text)) setEditing(false);
              }}
            />
          ) : hasText ? (
            <div className="bubble-row">
              <div className={`bubble${p.isOwn ? ' own' : ''}${row.isGroupStart ? ' caret' : ''}${pending ? ' pending' : ''}`}>
                <MessageBody message={post.message} ctx={p.ctx} />
                {post.editAt > 0 ? <span className="edited"> {t('channel.message.edited')}</span> : null}
              </div>
              {p.seen ? <SeenChecks level={p.seen} /> : null}
            </div>
          ) : null}
          {isEphemeral ? <span className="ephemeral-note">{t('channel.message.onlyVisibleToYou')}</span> : null}
          {props.attachments
            .filter((a) => !MessageAttachments.isEmpty(a))
            .map((a, i) => (
              <AttachmentCard key={i} attachment={a} ctx={p.ctx} onAction={(actionId, cookie, option) => p.actions.onAction(post.id, actionId, cookie, option)} />
            ))}
          {p.files?.length ? <Files files={p.files} /> : null}
          {embed && PostEmbed.isDrawable(embed) ? <EmbedCard embed={embed} /> : null}
          {failed ? (
            <div className="failed-line" role="alert">
              <Icon name="alertCircle" size={14} />
              {t('channel.error.sendMessage')}
              <button onClick={() => p.actions.onRetry(post.id)}>{t('common.retry')}</button>
              <button onClick={() => p.actions.onDiscard(post.id)}>{t('channel.messageDiscard')}</button>
            </div>
          ) : null}
          {p.chips?.length ? <Reactions chips={p.chips} onToggle={(name) => p.actions.onReact(post.id, name)} /> : null}
          {p.footer && !p.inThread ? <Footer footer={p.footer} names={p.footerNames} now={p.now} onOpen={() => p.actions.onOpenThread(p.footer!.rootId)} /> : null}
        </div>
        {post.state === PostState.Synced && !isEphemeral ? (
          <div className="hover-bar" role="toolbar">
            {p.actions.quickReactions.slice(0, 3).map((name) => (
              <button key={name} className="quick" title={`:${name}:`} onClick={() => p.actions.onReact(post.id, name)}>
                {EmojiCharacters.character(name) ?? `:${name}:`}
              </button>
            ))}
            {!p.inThread ? (
              <button title={t('channel.replyInThread')} onClick={() => p.actions.onOpenThread(post.rootId || post.id)}>
                <Icon name="reply" size={16} />
              </button>
            ) : null}
            <button
              title={t('web.msg.more')}
              aria-label={t('web.msg.more')}
              aria-haspopup="menu"
              onClick={(e) => {
                const r = e.currentTarget.getBoundingClientRect();
                setMenuAt({ x: r.right - 220, y: r.bottom + 4 });
              }}
            >
              <Icon name="more" size={18} strokeWidth={2.4} />
            </button>
          </div>
        ) : null}
        {menuAt ? (
          <MessageMenu
            at={menuAt}
            isOwn={p.isOwn}
            pinned={post.isPinned}
            saved={!!p.saved}
            inThread={!!p.inThread}
            quick={p.actions.quickReactions}
            onClose={() => setMenuAt(null)}
            onPick={(action) => {
              setMenuAt(null);
              switch (action.kind) {
                case 'react':
                  return p.actions.onReact(post.id, action.name);
                case 'reply':
                  return p.actions.onOpenThread(post.rootId || post.id);
                case 'copy':
                  return p.actions.onCopyText(post.message);
                case 'link':
                  return p.actions.onCopyLink(post.id);
                case 'pin':
                  return p.actions.onPin(post.id, !post.isPinned);
                case 'save':
                  return p.actions.onSave(post.id, !p.saved);
                case 'unread':
                  return p.actions.onMarkUnread(post.id);
                case 'edit':
                  return setEditing(true);
                case 'delete':
                  return p.actions.onDelete(post.id);
              }
            }}
          />
        ) : null}
      </article>
    </>
  );
});

type MenuAction =
  | { kind: 'react'; name: string }
  | { kind: 'reply' | 'copy' | 'link' | 'pin' | 'save' | 'unread' | 'edit' | 'delete' };

/** The message menu (hover "…" or right-click): the Mac's full context menu, in its order. */
function MessageMenu({ at, isOwn, pinned, saved, inThread, quick, onClose, onPick }: { at: { x: number; y: number }; isOwn: boolean; pinned: boolean; saved: boolean; inThread: boolean; quick: string[]; onClose(): void; onPick(a: MenuAction): void }) {
  const ref = useRef<HTMLDivElement>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [pos, setPos] = useState(at);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({ x: Math.max(8, Math.min(at.x, window.innerWidth - r.width - 8)), y: Math.max(8, Math.min(at.y, window.innerHeight - r.height - 8)) });
  }, [at]);
  useEffect(() => {
    const close = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && onClose();
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    setTimeout(() => window.addEventListener('mousedown', close));
    window.addEventListener('keydown', esc);
    window.addEventListener('blur', onClose);
    return () => {
      window.removeEventListener('mousedown', close);
      window.removeEventListener('keydown', esc);
      window.removeEventListener('blur', onClose);
    };
  }, [onClose]);
  // A PORTAL: rows use `content-visibility: auto`, whose paint containment clips anything inside a
  // row — a fixed menu rendered there was invisible.
  return createPortal(
    <div className="popover message-menu" role="menu" ref={ref} style={{ position: 'fixed', left: pos.x, top: pos.y, width: 230 }}>
      <div className="menu-quick">
        {quick.slice(0, 6).map((name) => (
          <button key={name} title={`:${name}:`} onClick={() => onPick({ kind: 'react', name })}>
            {EmojiCharacters.character(name) ?? `:${name}:`}
          </button>
        ))}
      </div>
      <div className="menu-sep" />
      {!inThread ? (
        <button className="menu-item" role="menuitem" onClick={() => onPick({ kind: 'reply' })}>
          <Icon name="reply" size={16} /> {t('channel.replyInThread')}
        </button>
      ) : null}
      <button className="menu-item" role="menuitem" onClick={() => onPick({ kind: 'copy' })}>
        <Icon name="copy" size={16} /> {t('channel.copyMessage')}
      </button>
      <button className="menu-item" role="menuitem" onClick={() => onPick({ kind: 'link' })}>
        <Icon name="link" size={16} /> {t('channel.action.copyLink')}
      </button>
      <button className="menu-item" role="menuitem" onClick={() => onPick({ kind: 'pin' })}>
        <Icon name="pin" size={16} /> {pinned ? t('channel.unpin') : t('channel.pin')}
      </button>
      <button className="menu-item" role="menuitem" onClick={() => onPick({ kind: 'save' })}>
        <Icon name="bookmark" size={16} /> {saved ? t('channel.unsave') : t('channel.save')}
      </button>
      <button className="menu-item" role="menuitem" onClick={() => onPick({ kind: 'unread' })}>
        <Icon name="envelope" size={16} /> {t('channel.markUnread')}
      </button>
      {isOwn ? (
        <>
          <div className="menu-sep" />
          <button className="menu-item" role="menuitem" onClick={() => onPick({ kind: 'edit' })}>
            <Icon name="pencilLine" size={16} /> {t('channel.editMessage')}
          </button>
          <button
            className="menu-item danger"
            role="menuitem"
            onClick={() => (confirmDelete ? onPick({ kind: 'delete' }) : setConfirmDelete(true))}
          >
            <Icon name="trash" size={16} /> {confirmDelete ? t('channel.deleteConfirmTitle') : t('channel.deleteMessage')}
          </button>
        </>
      ) : null}
    </div>,
    document.body,
  );
}

/** Editing in place: the bubble becomes the box. Enter saves, Shift+Enter breaks, Esc cancels. */
function InlineEditor({ initial, onSave, onCancel }: { initial: string; onSave(text: string): void; onCancel(): void }) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const [text, setText] = useState(initial);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, []);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = '0px';
    el.style.height = Math.min(260, el.scrollHeight) + 'px';
  }, [text]);
  return (
    <div className="inline-editor">
      <textarea
        ref={ref}
        className="text-input"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onCancel();
          if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
            e.preventDefault();
            if (text.trim()) onSave(text);
          }
        }}
      />
      <div className="inline-editor-hint">{t('web.msg.editHint')}</div>
    </div>
  );
}

function SeenChecks({ level }: { level: Exclude<SeenLevel, null> }) {
  // One muted check = on the server, unopened; two muted = some; two accented = all.
  return (
    <span className={`checks${level === 'all' ? ' all' : ''}`} title={level === 'all' ? t('channel.seen.byAll') : undefined}>
      <Icon name={level === 'sent' ? 'check' : 'checkDouble'} size={15} strokeWidth={1.8} />
    </span>
  );
}

function Reactions({ chips, onToggle }: { chips: ReactionChip[]; onToggle(name: string): void }) {
  return (
    <div className="reactions">
      {chips.map((c) => (
        <button key={c.emojiName} className={`reaction${c.mine ? ' mine' : ''}`} title={c.tooltip || `:${c.emojiName}:`} onClick={() => onToggle(c.emojiName)} aria-pressed={c.mine}>
          <span className="glyph">{EmojiCharacters.character(c.emojiName) ?? `:${c.emojiName}:`}</span>
          {c.count}
        </button>
      ))}
    </div>
  );
}

function Footer({ footer, names, now, onOpen }: { footer: ThreadFooter; names: (id: string) => { name: string; version: number }; now: number; onOpen(): void }) {
  const unread = footer.unreadReplies > 0 || footer.unreadMentions > 0;
  return (
    <button className={`thread-footer${unread ? ' unread' : ''}`} onClick={onOpen}>
      <span className="faces">
        {footer.avatarUserIds.map((id) => {
          const n = names(id);
          return <Avatar key={id} userId={id} name={n.name} size={20} version={n.version} />;
        })}
      </span>
      <span className="count">{footer.replyCount === 1 ? t('channel.replyCount.one') : tf('channel.replyCount.many', footer.replyCount)}</span>
      {footer.lastReplyAt ? <span className="last">{RelativeTime.format(footer.lastReplyAt, now, { yesterday: t('channelList.time.yesterday') })}</span> : null}
    </button>
  );
}

function AttachmentCard({ attachment: a, ctx, onAction }: { attachment: Attachment; ctx: BodyContext; onAction(actionId: string, cookie: string | null, option: string | null): void }) {
  const [busy, setBusy] = useState<string | null>(null);
  return (
    <div className="attachment-card">
      <div className="rule" style={a.color ? { background: a.color } : undefined} />
      <div className="body">
        {a.pretext ? <MessageBody message={a.pretext} ctx={ctx} /> : null}
        {a.authorName ? <div className="field-title">{a.authorName}</div> : null}
        {a.title ? (
          <div className="title">
            {a.titleLink ? (
              <a href={a.titleLink} target="_blank" rel="noreferrer noopener">
                {a.title}
              </a>
            ) : (
              a.title
            )}
          </div>
        ) : null}
        {a.text ? <MessageBody message={a.text} ctx={ctx} /> : null}
        {a.fields.length ? (
          <div className="fields">
            {a.fields.map((f, i) => (
              <div key={i} style={f.short ? undefined : { gridColumn: '1 / -1' }}>
                <div className="field-title">{f.title}</div>
                <MessageBody message={f.value} ctx={ctx} />
              </div>
            ))}
          </div>
        ) : null}
        {a.actions.length ? (
          <div className="actions-row">
            {a.actions.map((action) =>
              MessageAttachments.isSelect(action) ? (
                <select
                  key={action.id}
                  className="action-button"
                  defaultValue=""
                  onChange={(e) => e.target.value && onAction(action.id, action.cookie, e.target.value)}
                >
                  <option value="" disabled>
                    {action.name}
                  </option>
                  {action.options.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.text}
                    </option>
                  ))}
                </select>
              ) : (
                <button
                  key={action.id}
                  className={`action-button${action.style === 'primary' || action.style === 'good' || action.style === 'success' ? ' primary' : action.style === 'danger' || action.style === 'warning' ? ' danger' : ''}`}
                  disabled={busy === action.id}
                  onClick={() => {
                    setBusy(action.id);
                    onAction(action.id, action.cookie, null);
                    setTimeout(() => setBusy(null), 1500);
                  }}
                >
                  {action.name}
                </button>
              ),
            )}
          </div>
        ) : null}
        {a.footer ? <div className="field-title">{a.footer}</div> : null}
      </div>
    </div>
  );
}

function Files({ files }: { files: PostFileRecord[] }) {
  return (
    <>
      {files.map((f) => {
        const kind = AttachmentKind.classify(f.mimeType, f.name, f.width, f.height);
        if (kind === 'image') {
          // The footprint is reserved from the stored dimensions so nothing jumps when the
          // thumbnail lands (VISUAL-DESIGN: no layout shift); 320×350 is the desktop ceiling.
          const scale = Math.min(1, 320 / Math.max(1, f.width), 350 / Math.max(1, f.height));
          const w = Math.max(80, Math.round(f.width * scale));
          const h = Math.max(60, Math.round(f.height * scale));
          return (
            <a key={f.fileId} className="image-tile" href={mediaUrls.preview(f.fileId)} target="_blank" rel="noreferrer" style={{ width: w, height: h }} title={f.name}>
              <img src={mediaUrls.thumbnail(f.fileId)} alt={f.name} width={w} height={h} loading="lazy" decoding="async" style={{ width: w, height: h, objectFit: 'cover' }} />
            </a>
          );
        }
        return (
          <a key={f.fileId} className="file-card" href={mediaUrls.file(f.fileId, true)} target="_blank" rel="noreferrer" download={f.name}>
            <span className="file-icon">
              <Icon name="file" size={18} />
            </span>
            <span style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name || f.fileId}</div>
              <div className="meta">{AttachmentKind.sizeLabel(f.size)}</div>
            </span>
            <Icon name="download" size={16} style={{ marginLeft: 'auto', color: 'var(--text-muted)' }} />
          </a>
        );
      })}
    </>
  );
}

function EmbedCard({ embed }: { embed: PostEmbedValue }) {
  const isPermalink = embed.type === PostEmbed.typePermalink;
  return (
    <a className="attachment-card" href={embed.url || '#'} target="_blank" rel="noreferrer noopener" style={{ textDecoration: 'none', color: 'inherit' }}>
      <div className="rule" style={{ background: 'var(--accent)' }} />
      <div className="body">
        <div className="field-title">{isPermalink ? embed.channelDisplayName : embed.siteName}</div>
        {embed.title ? <div className="title">{embed.title}</div> : null}
        {embed.description ? <div style={{ color: 'var(--text-secondary)' }}>{embed.description}</div> : null}
      </div>
    </a>
  );
}
